import { RequestStatus } from 'features/request/requestSlice';

import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { useIsUniqueGroupsActsName } from '../components/WorkOrderActsKc2ListGroupManager';

export const useCommonActsKC2GroupManager = <
    TField extends {
        headerCheckbox: boolean | 'intermediate' | undefined;
        actKC2Ids: Record<string, boolean>;
        groupName: string | undefined;
    },
>({
    actKC2GroupId,
    defaultData,
    groupManagerOpened,
    workOrderId,
    uniqueGroupNameQuery,
}: {
    actKC2GroupId: number | null;
    defaultData: TField | undefined | null;
    groupManagerOpened: boolean;
    workOrderId: number;
    uniqueGroupNameQuery: string | TypedDocumentNode<any, any>;
}) => {
    const {
        control,
        setValue,
        watch,
        handleSubmit,
        formState: { isDirty },
        reset,
        setError,
        clearErrors,
    } = useForm<{
        headerCheckbox: boolean | 'intermediate' | undefined;
        actKC2Ids: Record<string, boolean>;
        groupName: string | undefined;
    }>({
        shouldUnregister: true,
    });

    const { headerCheckbox: headerCheckboxValue } = watch();

    const groupName = useWatch({ control, name: 'groupName' });

    const { isNotUniqueActKC2GroupName, currentStatusUniqueName, makeResetIsUniqueActKC2GroupName } =
        useIsUniqueGroupsActsName(uniqueGroupNameQuery, groupName, workOrderId, actKC2GroupId);

    useEffect(() => {
        if (groupName && currentStatusUniqueName === RequestStatus.SUCCESS && isNotUniqueActKC2GroupName) {
            setError('groupName', { message: 'Группа с таким именем уже существует' });
        } else {
            clearErrors('groupName');
        }
    }, [clearErrors, isNotUniqueActKC2GroupName, groupName, setError, currentStatusUniqueName]);

    useEffect(
        () => () => {
            makeResetIsUniqueActKC2GroupName(null);
        },
        [makeResetIsUniqueActKC2GroupName],
    );

    const actKC2IdsFromWatch = useWatch({
        control,
        name: 'actKC2Ids',
    });

    const allChecked = actKC2IdsFromWatch && Object.values(actKC2IdsFromWatch).every((item) => item);
    const someChecked = actKC2IdsFromWatch && Object.values(actKC2IdsFromWatch).some((item) => item);
    const allUnchecked = actKC2IdsFromWatch && Object.values(actKC2IdsFromWatch).every((item) => !item);

    useEffect(() => {
        if (groupManagerOpened && actKC2IdsFromWatch) {
            if (allChecked) {
                setValue('headerCheckbox', true);
            } else if (someChecked) {
                setValue('headerCheckbox', 'intermediate');
            } else if (!someChecked && !allChecked) {
                setValue('headerCheckbox', false);
            }
        }
    }, [actKC2IdsFromWatch, allChecked, allUnchecked, groupManagerOpened, setValue, someChecked]);

    useEffect(() => {
        if (groupManagerOpened && defaultData?.actKC2Ids) {
            if (headerCheckboxValue === true) {
                Object.keys(defaultData.actKC2Ids).forEach((key) => {
                    setValue(`actKC2Ids.${key}`, true);
                });
            } else if (headerCheckboxValue === false) {
                Object.keys(defaultData.actKC2Ids).forEach((key) => {
                    setValue(`actKC2Ids.${key}`, false);
                });
            }
        }
    }, [defaultData?.actKC2Ids, groupManagerOpened, headerCheckboxValue, setValue]);

    useEffect(() => {
        if (groupManagerOpened && defaultData && actKC2GroupId) {
            reset(defaultData);
        }
        if (!groupManagerOpened) {
            reset();
        }
    }, [actKC2GroupId, defaultData, groupManagerOpened, reset]);

    return {
        control,
        handleSubmit,
        allUnchecked,
        isNotUniqueActKC2GroupName,
        isDirty,
    };
};
